import { useCallback, useContext, useMemo, type CSSProperties } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { openOfferPageRoomGalleryLightbox } from 'components/OfferPage/OfferPageRoomGallery'
import ModalContext from 'contexts/ModalContext'

const Thumbnail = styled(Clickable)`
  position: relative;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
`

const GalleryIcon = styled.span`
  position: absolute;
  inset-inline-end: 0;
  bottom: 0;
  display: grid;
  place-items: center;
  width: ${rem(28)};
  height: ${rem(28)};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  background-color: color-mix(in srgb, ${props => props.theme.palette.neutral.surface.darkBackground} 60%, transparent);
`

interface Props {
  room: { name: string, images: Array<App.Image> }
  width: number
  height: number
  className?: string
}

function PackageSelectionRoomThumbnail({ room, width, height, className }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const handleOpenGallery = useCallback(() => {
    openOfferPageRoomGalleryLightbox(showModal, room.images)
  }, [room.images, showModal])
  const image = room.images[0]
  const thumbnailStyle = useMemo<CSSProperties>(() => ({ width: rem(width), height: rem(height) }), [width, height])
  if (!image?.id) return null

  return <Thumbnail
    className={className}
    style={thumbnailStyle}
    onClick={handleOpenGallery}
    aria-label={intl.formatMessage({
      id: 'qc9c0f45uC1h8ar1CMDV',
      defaultMessage: 'View photos of {roomName}',
      description: 'Accessible name for the thumbnail opening the image gallery of a property room',
    }, { roomName: room.name })}
  >
    <ResponsiveImage id={image.id} alt={room.name} width={width} height={height} mobileWidth={`${width}px`} aspectRatio={`${width}:${height}`} quality="good" fit="center" />
    <GalleryIcon aria-hidden="true"><LineImagesIcon size="S" /></GalleryIcon>
  </Thumbnail>
}

export default PackageSelectionRoomThumbnail
